import { useState } from 'react';

import { adminApi } from '@/api';
import { Badge, Button, Card, CardHeader, Icon } from '@/components/ui';
import { DocumentViewer } from '@/features/verification/DocumentViewer';
import { ADMIN_DOCUMENT_LABELS, DOCUMENT_TONES } from '@/features/verification/status';
import { formatDate, formatFileSize } from '@/utils/format';

import { RejectReasonModal } from './RejectReasonModal';
import { useDocumentDecision } from './useDocumentDecision';

/** What the student typed, which the uploaded file is checked against. */
function claim(row) {
  return [
    row.issuer,
    row.issue_date && `Issued ${formatDate(row.issue_date)}`,
    row.expiry_date && `expires ${formatDate(row.expiry_date)}`,
    row.credential_id && `ID ${row.credential_id}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * A student's certificates on their admin record. Each one with a file can be
 * opened (audited), verified or rejected with a reason -- the same calls as
 * the Certificates tab of Verify documents. One without a file has nothing to
 * check yet, so it only says so. The student-typed credential link is not
 * shown: it is not proof, and it can point anywhere.
 */
export function StudentCertificationsCard({ student }) {
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const { approve, reject } = useDocumentDecision({
    kind: 'certification',
    noun: 'certificate',
    student,
  });

  const rows = student.certifications ?? [];

  return (
    <Card>
      <CardHeader
        title="Certifications"
        description="Open the uploaded certificate, compare it with the details, then verify or reject it."
      />
      <ul className="divide-line divide-y">
        {rows.map((row) => {
          const hasFile = Boolean(row.file_name);
          const status = row.verification_status ?? 'UNVERIFIED';
          const busy = approve.isPending && approve.variables?.id === row.id;
          return (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
            >
              <div className="flex min-w-0 flex-1 basis-64 items-start gap-3">
                <span
                  className={
                    status === 'VERIFIED'
                      ? 'bg-success-50 text-success-700 grid size-10 shrink-0 place-items-center rounded-lg'
                      : 'bg-brand-50 text-brand-600 grid size-10 shrink-0 place-items-center rounded-lg'
                  }
                >
                  <Icon name={status === 'VERIFIED' ? 'shieldCheck' : 'idBadge'} size={18} />
                </span>
                <div className="min-w-0">
                  <p className="text-ink text-sm font-medium">{row.name}</p>
                  {claim(row) && <p className="text-ink-muted text-xs">{claim(row)}</p>}
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {hasFile ? (
                      <>
                        <Badge dot tone={DOCUMENT_TONES[status] ?? 'neutral'}>
                          {ADMIN_DOCUMENT_LABELS[status] ?? status}
                        </Badge>
                        <span className="text-ink-muted text-xs">
                          {row.file_name} · {formatFileSize(row.size_bytes)}
                          {row.verification_submitted_at &&
                            ` · Sent ${formatDate(row.verification_submitted_at)}`}
                        </span>
                      </>
                    ) : (
                      <Badge dot tone="neutral">
                        No file uploaded
                      </Badge>
                    )}
                  </div>
                  {status === 'REJECTED' && row.verification_rejection_reason && (
                    <p className="text-danger-600 mt-1 text-xs">
                      Rejected: {row.verification_rejection_reason}
                    </p>
                  )}
                </div>
              </div>
              {hasFile && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    title={`Open ${row.file_name}`}
                    onClick={() => setViewing(row)}
                  >
                    <Icon name="eye" size={14} />
                    View file
                  </Button>
                  {status !== 'REJECTED' && (
                    <Button
                      variant="tintDanger"
                      size="sm"
                      title={`Reject ${row.name} with a reason`}
                      onClick={() => setRejecting(row)}
                    >
                      <Icon name="close" size={14} />
                      Reject
                    </Button>
                  )}
                  {status !== 'VERIFIED' && (
                    <Button
                      variant="tintPositive"
                      size="sm"
                      title={`Mark ${row.name} as verified`}
                      isLoading={busy}
                      onClick={() => approve.mutate(row)}
                    >
                      <Icon name="check" size={14} />
                      Verify
                    </Button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Shown in the page, not downloaded. Each open is audited. */}
      <DocumentViewer
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title={viewing ? `${student.full_name}'s certificate: ${viewing.name}` : ''}
        fileName={viewing?.file_name}
        contentType={viewing?.content_type}
        queryKey={['certification', viewing?.id]}
        loadUrl={(options) => adminApi.getDocumentDownloadUrl('certification', viewing.id, options)}
      />

      <RejectReasonModal
        open={rejecting !== null}
        title={rejecting ? `Reject ${student.full_name}'s certificate` : ''}
        placeholder="The name on the certificate does not match the student"
        isLoading={reject.isPending}
        onClose={() => setRejecting(null)}
        onSubmit={(reason) => reject.mutateAsync({ document: rejecting, reason })}
      />
    </Card>
  );
}
